import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsomorphicLayoutEffect } from 'react-use';
import type { Location } from '@remix-run/router';

const storage: Record<string, number> = {};

type UseScrollRestorationProps = {
    buildLocationKey: (location: Location) => string | null;
};

const RESTORATION_DEBOUNCE_TIME = 500;
const RESTORATION_MAX_TIME = 1000;
const SCROLL_REACHABILITY_MARGIN_PX = 1;

export const useScrollRestoration = (props: UseScrollRestorationProps) => {
    const { buildLocationKey } = props;
    const location = useLocation();
    const locationPath = location.pathname + location.search;
    const isLastLocationDialogRef = useRef(false);
    const lastNonDialogLocationPathRef = useRef(locationPath);
    const activeLocationKeyRef = useRef(location.key);

    activeLocationKeyRef.current = location.key;

    useIsomorphicLayoutEffect(() => {
        if (location.state?.dialogName) {
            isLastLocationDialogRef.current = true;

            return () => {};
        }

        const wasLastLocationDialog = isLastLocationDialogRef.current;
        isLastLocationDialogRef.current = false;
        const isReturningToLastNonDialogLocation =
            wasLastLocationDialog && lastNonDialogLocationPathRef.current === locationPath;
        lastNonDialogLocationPathRef.current = locationPath;
        const key = buildLocationKey(location);

        // Если ключ null, значит страница в blacklist - не восстанавливаем и не сохраняем скролл
        if (key === null) {
            return () => {};
        }

        let saveEnabled = false;
        const locationKey = location.key;
        const onWindowScroll = () => {
            if (saveEnabled && activeLocationKeyRef.current === locationKey) {
                storage[key] = window.scrollY;
            }
        };

        if (isReturningToLastNonDialogLocation) {
            saveEnabled = true;
            window.addEventListener('scroll', onWindowScroll, { passive: true });

            return () => {
                window.removeEventListener('scroll', onWindowScroll);
            };
        }

        const scrollTop = storage[key] ?? 0;
        const documentElement = window.document.documentElement;
        const previousHeight = documentElement.style.getPropertyValue('height');
        const previousHeightPriority = documentElement.style.getPropertyPriority('height');
        // Чтобы scrollTop был достижим, высота документа должна быть не меньше scrollTop + viewport.
        // Запас в один пиксель защищает от ограничения позиции браузером из-за округления.
        const applyPlaceholderHeight = () => {
            const minimumDocumentHeight = scrollTop + documentElement.clientHeight;
            const placeholderHeight = Math.ceil(
                minimumDocumentHeight + SCROLL_REACHABILITY_MARGIN_PX,
            );

            documentElement.style.setProperty(
                'height',
                `${String(placeholderHeight)}px`,
                'important',
            );
        };
        const restoreDocumentHeight = () => {
            if (previousHeight) {
                documentElement.style.setProperty('height', previousHeight, previousHeightPriority);
            } else {
                documentElement.style.removeProperty('height');
            }
        };

        applyPlaceholderHeight();
        window.scrollTo({ top: scrollTop });
        const restorationStartedAt = Date.now();
        let restoreFrameId = 0;
        let finishRestorationTimerId: number | undefined;
        let enableSaveTimerId: number | undefined;
        const enableSaving = () => {
            enableSaveTimerId = window.setTimeout(() => {
                if (activeLocationKeyRef.current === locationKey) {
                    saveEnabled = true;
                }
            }, 0);
        };
        const finishRestoration = () => {
            if (activeLocationKeyRef.current !== locationKey) {
                return;
            }

            const reachedTargetWithPlaceholder = Math.abs(window.scrollY - scrollTop) <= 1;

            if (!reachedTargetWithPlaceholder) {
                restoreDocumentHeight();
                storage[key] = window.scrollY;
                saveEnabled = true;

                return;
            }

            restoreDocumentHeight();

            // Удаление placeholder может синхронно ограничить scrollY, если контент ещё слишком короткий.
            // Поэтому повторно проверяем позицию уже с реальной высотой документа.
            const reachedTargetWithContent = Math.abs(window.scrollY - scrollTop) <= 1;

            if (
                reachedTargetWithContent ||
                Date.now() - restorationStartedAt >= RESTORATION_MAX_TIME
            ) {
                enableSaving();

                return;
            }

            applyPlaceholderHeight();
            window.scrollTo({ top: scrollTop });
            restoreFrameId = window.requestAnimationFrame(finishRestoration);
        };
        const restoreTimerId = window.setTimeout(() => {
            if (activeLocationKeyRef.current !== locationKey) {
                return;
            }

            window.scrollTo({ top: scrollTop });
            finishRestorationTimerId = window.setTimeout(
                finishRestoration,
                RESTORATION_DEBOUNCE_TIME,
            );
        }, 0);

        window.addEventListener('scroll', onWindowScroll, { passive: true });

        return () => {
            window.cancelAnimationFrame(restoreFrameId);
            window.clearTimeout(restoreTimerId);
            window.clearTimeout(finishRestorationTimerId);
            window.clearTimeout(enableSaveTimerId);
            restoreDocumentHeight();
            window.removeEventListener('scroll', onWindowScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);
};
